/**
 * GFG-273 (Campaigns tab, GFG Admin) — the Client cell's popover. Shows the campaign's account
 * lead (the GfG-side owner), deliberately NOT the prototype's org contact — an admin opening this
 * from the Campaigns table wants to know who runs the account, not who to email at the client.
 * Externally anchored like CampaignRowMenu: the caller renders the Client button and owns
 * `anchorEl`.
 */
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Alert, IconButton, Popper } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { AdminCampaignRow } from "@/interfaces/adminCampaigns";

export interface ClientContactPopoverProps {
  campaign: AdminCampaignRow;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

// Only this popover renders these four strings, so they stay local — the same call CampaignsPanel
// makes for its own PANEL_COPY. Promote them to constants/adminCampaigns.ts if a second unit needs
// them.
const POPOVER_COPY = {
  unassigned: "Unassigned",
  copyEmailLabel: "Copy email address",
  copySuccess: "Email copied to clipboard",
  copyFailure: "Couldn't copy — your browser blocked clipboard access.",
} as const;

/** Writes to the clipboard, resolving to whether it landed — never fake a success. */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ClientContactPopover({
  campaign,
  anchorEl,
  onClose,
}: ClientContactPopoverProps) {
  const isOpen = Boolean(anchorEl);
  const panelRef = useRef<HTMLDivElement>(null);
  const [copyResult, setCopyResult] = useState<"success" | "failure" | null>(null);
  const hasLead = campaign.accountLead !== null && campaign.accountLeadEmail !== null;

  // Escape and a genuine outside click both close; a click on the Client button
  // itself is that button's own toggle, not an "outside" click. It lives in the caller's tree,
  // so a ClickAwayListener ref can't exclude it — hence the manual anchorEl containment check.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      onClose();
      anchorEl?.focus();
    }
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (anchorEl?.contains(target) || panelRef.current?.contains(target)) return;
      onClose();
      anchorEl?.focus();
    }
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isOpen, anchorEl, onClose]);

  // Mirrors ColumnConfigPopover: Popper unmounts the panel on close without restoring focus by
  // itself, so opening focuses the panel and closing (above) focuses the trigger back.
  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  // Clears on close (a different row's lead must never show a stale result) and auto-hides
  // after a few seconds so the feedback reads as transient, not a permanent label.
  useEffect(() => {
    if (!isOpen) setCopyResult(null);
  }, [isOpen]);
  useEffect(() => {
    if (!copyResult) return;
    const timer = setTimeout(() => setCopyResult(null), 3000);
    return () => clearTimeout(timer);
  }, [copyResult]);

  async function handleCopy() {
    if (!campaign.accountLeadEmail) return;
    const ok = await writeToClipboard(campaign.accountLeadEmail);
    setCopyResult(ok ? "success" : "failure");
  }

  return (
    <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Account lead contact"
        className="mt-1.5 w-[280px] max-w-[calc(100vw-32px)] rounded-[10px] border border-smoke
          bg-white p-4 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]"
      >
        {hasLead ? (
          <>
            <p className="font-lato text-sm font-semibold text-rodeo-black">
              {campaign.accountLead}
            </p>
            <div className="mt-1 flex items-center gap-1">
              <p className="min-w-0 flex-1 truncate font-lato text-sm text-graphite">
                {campaign.accountLeadEmail}
              </p>
              <IconButton
                size="small"
                aria-label={POPOVER_COPY.copyEmailLabel}
                title={POPOVER_COPY.copyEmailLabel}
                onClick={handleCopy}
              >
                {copyResult === "success" ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </div>
            {copyResult && (
              <Alert
                severity={copyResult === "success" ? "success" : "error"}
                sx={{ mt: 1, py: 0 }}
              >
                {copyResult === "success" ? POPOVER_COPY.copySuccess : POPOVER_COPY.copyFailure}
              </Alert>
            )}
          </>
        ) : (
          <p className="font-lato text-sm text-graphite">{POPOVER_COPY.unassigned}</p>
        )}
      </div>
    </Popper>
  );
}
